import { useState, useEffect, useRef } from "react";
import "./CAS1.css";

interface CAS1Props {
  onGateway: (accepted: boolean) => void;
  onChatbotTrigger: (visible: boolean) => void;
}

export default function CAS1({ onChatbotTrigger }: CAS1Props) {
  const [bpm, setBpm] = useState(92);
  const [bpmClass, setBpmClass] = useState("high");
  const [loaAnimated, setLoaAnimated] = useState(false);

  const hbSectionRef = useRef<HTMLElement>(null);
  const loaSectionRef = useRef<HTMLElement>(null);
  const gatewayRef = useRef<HTMLElement>(null);

  // Intersection Observer for scroll reveals
  useEffect(() => {
    const caLane = document.querySelector(".ca-lane");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -40px 0px", root: caLane }
    );

    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  // BPM countdown on scroll
  useEffect(() => {
    const caLane = document.querySelector(".ca-lane");

    const updateBPM = () => {
      if (!hbSectionRef.current || !caLane) return;

      const rect = hbSectionRef.current.getBoundingClientRect();
      const vh = caLane.clientHeight;
      const progress = Math.max(
        0,
        Math.min(1, (vh - rect.top) / (rect.height + vh * 0.3))
      );

      const newBpm = Math.round(92 - progress * 34);
      setBpm(newBpm);

      if (newBpm > 80) setBpmClass("high");
      else if (newBpm > 68) setBpmClass("mid");
      else setBpmClass("low");
    };

    const scrollTarget = caLane || window;
    scrollTarget.addEventListener("scroll", updateBPM, { passive: true });
    return () => scrollTarget.removeEventListener("scroll", updateBPM);
  }, []);

  // LOA heartbeat animation trigger
  useEffect(() => {
    const caLane = document.querySelector(".ca-lane");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setLoaAnimated(true);
          }
        });
      },
      { threshold: 0.3, root: caLane }
    );

    if (loaSectionRef.current) {
      observer.observe(loaSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  // Chatbot trigger — fires to App.tsx when user reaches final image
  useEffect(() => {
    const caLane = document.querySelector(".ca-lane");

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          onChatbotTrigger(entry.isIntersecting);
        });
      },
      { threshold: 0.3, root: caLane }
    );

    if (gatewayRef.current) {
      observer.observe(gatewayRef.current);
    }

    return () => observer.disconnect();
  }, [onChatbotTrigger]);

  return (
    <>
      <div className="grain" />
      <div className="ambient-glow" />

      {/* 1. HERO */}
      <section className="cas1-hero">
        <div
          className="cas1-hero-bg"
          style={{ backgroundImage: "url(/images/IMG_5800.PNG)" }}
        />
        <div className="cas1-hero-overlay" />
        <div className="cas1-hero-content">
          <div className="hook-label">LOA</div>
          <h1>
            <span className="line">The city takes.</span>
            <span className="line">
              <em className="coral">We give back.</em>
            </span>
          </h1>
          <p className="hook-sub">
            Urban reset infrastructure. Micro sanctuaries embedded where the
            city needs them most.
          </p>
        </div>
        <div className="scroll-cue">
          <span>Scroll</span>
          <div className="line" />
        </div>
      </section>

      {/* 2. PROBLEM */}
      <section className="cas1-problem">
        <p className="reveal">The city doesn't stop.</p>
        <p className="reveal reveal-d1">
          The notifications don't stop. The demands don't stop.
        </p>
        <p className="reveal reveal-d2">
          You're expected to perform - constantly, flawlessly, endlessly.
        </p>
        <p className="reveal reveal-d3">
          But where do you go when you need to stop?
        </p>
        <p className="reveal reveal-d4 dim italic">
          Not home. Not the office. Not a cafe where you're still on stage.
        </p>
        <p className="reveal reveal-d5 accent">
          Nowhere. Because that infrastructure doesn't exist.
        </p>
      </section>

      {/* 3. STATS */}
      <section className="cas1-stats">
        <div className="stats-grid">
          <div className="stat reveal">
            <div className="stat-number">74%</div>
            <div className="stat-desc">
              of city dwellers report feeling overwhelmed by daily cognitive
              demands
            </div>
          </div>
          <div className="stat reveal reveal-d1">
            <div className="stat-number">4h 14m</div>
            <div className="stat-desc">
              average daily screen time - brains never switch off
            </div>
          </div>
          <div className="stat reveal reveal-d2">
            <div className="stat-number">Zero</div>
            <div className="stat-desc">
              dedicated decompression spaces in the average city centre
            </div>
          </div>
          <div className="stat reveal reveal-d3">
            <div className="stat-number">20 min</div>
            <div className="stat-desc">
              the research-backed window for measurable stress reduction
            </div>
          </div>
        </div>
      </section>

      {/* 4. HEARTBEAT NARRATIVE */}
      <section className="cas1-heartbeat" ref={hbSectionRef}>
        <div className={`bpm-float ${bpmClass}`}>
          <span className="bpm-num">{bpm}</span>
          <span className="bpm-unit">BPM</span>
          <span className="bpm-dot" />
        </div>

        <div className="hb-step reveal">
          <p>
            Heart rate climbing. Thoughts racing. The morning has already won.
          </p>
        </div>

        <div className="hb-step reveal">
          <p>
            You reach for your phone. Open the app. A LOA pod, four minutes
            away.
          </p>
        </div>

        <div className="hb-step reveal">
          <p>You walk in. The door closes. The noise drops. The light dims.</p>
        </div>

        <div className="hb-step reveal">
          <p>Eyes close. Breathing slows. The fog starts to lift.</p>
        </div>

        <div className="hb-step reveal">
          <p className="coral">
            Twenty minutes. Clarity restored. You step back out - different.
          </p>
        </div>

        <div className="hb-closer reveal">
          <p>
            The science is settled. The benefits are real. What's missing is the
            space.
          </p>
        </div>
      </section>

      {/* 5. IMAGE BREAK */}
      <section
        className="cas1-image-break"
        style={{ backgroundImage: "url(/images/IMG_5768.PNG)" }}
      />

      {/* 6. LOA REVEAL */}
      <section className="cas1-loa-reveal" ref={loaSectionRef}>
        <div className="loa-label reveal">Introducing</div>

        <div className="loa-logo-block">
          <div className="loa-heartbeat-icon">
            <svg viewBox="0 0 60 40">
              <path
                className={`loa-hb-path ${loaAnimated ? "animate" : ""}`}
                d="M0,20 L12,20 L16,20 L20,8 L24,32 L28,14 L32,26 L36,20 L44,20 L48,20 L60,20"
              />
            </svg>
          </div>
          <div className="loa-name reveal reveal-d1">L O A</div>
        </div>

        <p className="loa-copy reveal reveal-d2">
          <em>
            An operating platform of micro sanctuaries embedded in urban
            environments.
          </em>
        </p>
        <p className="loa-copy reveal reveal-d3">
          Private spaces designed for one purpose: decompression. You book it.
          It's yours. No one else gets access.
        </p>
        <p className="loa-copy reveal reveal-d4">
          Step inside. Twenty minutes. Step back out.
        </p>
        <p className="loa-copy dim reveal reveal-d5">
          <em>
            Your sanctuary. Your reset. Infrastructure for the modern city
            dweller.
          </em>
        </p>
      </section>

      {/* 7. HOW IT WORKS */}
      <section className="cas1-how-it-works">
        <span className="section-label reveal">How it works</span>
        <h2 className="section-title reveal reveal-d1">
          Three steps. No friction.
        </h2>

        <div className="steps-grid">
          <div className="step reveal reveal-d2">
            <div className="step-number">1</div>
            <h3>FIND</h3>
            <p>Open the app. See available spaces nearby. Pick your moment.</p>
          </div>
          <div className="step reveal reveal-d3">
            <div className="step-number">2</div>
            <h3>BOOK</h3>
            <p>
              Reserve your 20 minutes. It's exclusively yours. Contactless. No
              queue.
            </p>
          </div>
          <div className="step reveal reveal-d4">
            <div className="step-number">3</div>
            <h3>RESET</h3>
            <p>
              Step in. Door closes. The city disappears. Twenty minutes later,
              you're back.
            </p>
          </div>
        </div>
      </section>

      {/* 8. TWO PRODUCTS */}
      <section className="cas1-two-products">
        <span className="section-label reveal">The product line</span>
        <h2 className="section-title reveal reveal-d1">
          Two products. One mission.
        </h2>

        <div className="products-grid">
          <div className="product reveal reveal-d2">
            <img src="/images/IMG_5789.PNG" alt="Breathe Pods" />
            <div className="product-content">
              <span className="product-label">BREATHE PODS</span>
              <h3>Individual Reset Spaces</h3>
              <p>
                One person. Twenty minutes. Deploy anywhere. Transit hubs,
                corporate offices, shopping centres, gyms. Your private
                sanctuary - booked, sealed, yours alone.
              </p>
            </div>
          </div>
          <div className="product reveal reveal-d3">
            <img src="/images/IMG_5761.PNG" alt="Breathe Bars" />
            <div className="product-content">
              <span className="product-label">BREATHE BARS</span>
              <h3>Full Experience Setups</h3>
              <p>
                Multiple private pods, social infrastructure, kids calm zone,
                self-serve bar. Higher density, higher impact. The product that
                delivers the mission.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 9. PURE AIR TECHNOLOGY */}
      <section className="cas1-pure-air">
        <div className="pure-air-visual">
          <div className="air-circles">
            <div className="circle" />
            <div className="circle" />
            <div className="circle" />
          </div>
          <span className="air-label">HEPA + ACTIVATED CARBON FILTRATION</span>
        </div>

        <div className="pure-air-content">
          <span className="section-label reveal">Pure air technology</span>
          <h2 className="reveal reveal-d1">
            The city's air stays outside.{" "}
            <span className="coral">Yours is filtered.</span>
          </h2>
          <p className="reveal reveal-d2">
            Every LOA pod runs medical-grade air filtration. HEPA and activated
            carbon remove 99.97% of particulates, pollutants, and allergens. In
            a city where air quality is measurably harmful, your twenty minutes
            are spent breathing the cleanest air available.
          </p>
          <p className="reveal reveal-d3">
            <strong>Not a feature. A health intervention.</strong>
          </p>

          <div className="air-stats reveal reveal-d4">
            <div className="air-stat">
              <div className="air-stat-number">99.97%</div>
              <p>Particulate removal via HEPA filtration</p>
            </div>
            <div className="air-stat">
              <div className="air-stat-number">PM2.5</div>
              <p>Fine particles, NOx, and VOCs all neutralised</p>
            </div>
          </div>
        </div>
      </section>

      {/* 10. FOUR WAYS TO RESET */}
      <section className="cas1-experience-types">
        <span className="section-label reveal">Experience types</span>
        <h2 className="section-title reveal reveal-d1">Four ways to reset</h2>

        <div className="experiences-grid">
          <div className="experience reveal reveal-d2">
            <img src="/images/IMG_5789.PNG" alt="Solo" />
            <div className="experience-content">
              <span className="exp-label">EXPERIENCE 01</span>
              <h3>Solo</h3>
              <p>
                Your private sanctuary. Controlled light, sound, temperature.
                Twenty minutes that belong entirely to you.
              </p>
            </div>
          </div>
          <div className="experience reveal reveal-d3">
            <img src="/images/IMG_5763.PNG" alt="Mother Moment" />
            <div className="experience-content">
              <span className="exp-label">EXPERIENCE 02</span>
              <h3>Mother Moment</h3>
              <p>
                Parent respite. Kids decompress alongside in a calm zone. Not
                separated - together but apart.
              </p>
            </div>
          </div>
          <div className="experience reveal reveal-d4">
            <img src="/images/IMG_5792.PNG" alt="Vista" />
            <div className="experience-content">
              <span className="exp-label">EXPERIENCE 03</span>
              <h3>Vista</h3>
              <p>
                Reset with a view. The world outside, stillness inside.
                Perspective restored.
              </p>
            </div>
          </div>
          <div className="experience reveal reveal-d5">
            <img src="/images/IMG_5788.PNG" alt="The Grove" />
            <div className="experience-content">
              <span className="exp-label">EXPERIENCE 04</span>
              <h3>The Grove</h3>
              <p>
                Nature embedded in the city. Biophilic refuge. Living walls,
                natural textures. Urban forest bathing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 11. YOUR WELLBEING COMPANION — interactive app preview */}
      <section className="cas1-app-showcase">
        <div className="app-heartbeat-icon">
          <svg viewBox="0 0 80 20">
            <path d="M0,10 L20,10 L25,10 L30,3 L35,17 L40,10 L60,10 L65,10 L80,10" />
          </svg>
        </div>
        <h2 className="reveal">Your wellbeing companion</h2>
        <p className="reveal reveal-d1">
          LOA lives on your phone. It learns how you feel, finds the right
          space, tracks your recovery, and rewards you for looking after
          yourself.
        </p>

        <div className="phone-frame-wrap reveal reveal-d2">
          <div className="phone-frame">
            <div className="phone-notch" />
            <div className="phone-screen">
              <iframe
                src="/app-preview.html"
                title="LOA App Preview"
                scrolling="yes"
                frameBorder="0"
              />
            </div>
            <div className="phone-home-bar" />
          </div>
          <p className="phone-hint">Interactive - scroll to explore</p>
        </div>
      </section>

      {/* 12. LIFESTYLE BREAK */}
      <section
        className="cas1-image-break"
        style={{ backgroundImage: "url(/images/IMG_5793.PNG)" }}
      />

      {/* 13. USE CASE: PROFESSIONAL */}
      <section className="cas1-use-case">
        <img
          src="/images/IMG_5794.PNG"
          alt="The Professional"
          className="reveal"
        />
        <div className="use-case-content">
          <span className="use-case-label reveal reveal-d1">
            THE PROFESSIONAL
          </span>
          <p className="use-case-copy reveal reveal-d2">
            <em>Aggressive morning. Back-to-back calls. Inbox at 247.</em>
          </p>
          <p className="reveal reveal-d3">
            Lunch isn't for eating - it's for recalibrating. Twenty minutes in a
            LOA pod. No input. No output. Just reset.
          </p>
        </div>
      </section>

      {/* 14. USE CASE: TRAVELLER */}
      <section className="cas1-use-case reverse">
        <div className="use-case-content">
          <span className="use-case-label reveal reveal-d1">THE TRAVELLER</span>
          <p className="use-case-copy reveal reveal-d2">
            <em>New city. Delayed flight. Three hours until your meeting.</em>
          </p>
          <p className="reveal reveal-d3">
            You need a space to gather your thoughts. LOA gives you that space.
          </p>
        </div>
        <img
          src="/images/IMG_5800.PNG"
          alt="The Traveller"
          className="reveal"
        />
      </section>

      {/* 15. USE CASE: FAMILY */}
      <section className="cas1-use-case">
        <img src="/images/IMG_5763.PNG" alt="The Family" className="reveal" />
        <div className="use-case-content">
          <span className="use-case-label reveal reveal-d1">THE FAMILY</span>
          <p className="use-case-copy reveal reveal-d2">
            <em>
              You're in the city with the kids. It started well. But three hours
              in, the noise is stacking up. They're overstimulated. You're
              overstimulated. Everyone needs to stop.
            </em>
          </p>
          <p className="reveal reveal-d3">
            But where? The cafe is all hard edges and side-eyes. The restaurant
            doesn't want you. The hotel lobby makes you feel like you're
            trespassing. There's nowhere designed for this moment.
          </p>
          <p className="reveal reveal-d4">
            LOA is that space. The kids land in a calm zone - soft light,
            tactile surfaces, permission to be still. You step into a pod.
            Twenty minutes. Door closes. The city disappears. When you step back
            out, everyone's reset. The day continues.
          </p>
        </div>
      </section>

      {/* 16. PRODUCT FEATURES */}
      <section className="cas1-product-features">
        <span className="section-label reveal">Product features</span>
        <h2 className="section-title reveal reveal-d1">Built for deployment</h2>

        <div className="features-grid">
          <div className="feature-column reveal reveal-d2">
            <h3>Breathe Pod</h3>
            <span className="feature-subhead">50 SQFT • INDIVIDUAL</span>
            <ul>
              <li>50 sqft self-contained footprint</li>
              <li>Fully private - booked, sealed, yours alone</li>
              <li>Acoustic isolation, full soundproofing</li>
              <li>Medical-grade air filtration - HEPA + carbon</li>
              <li>Climate control - individual temperature</li>
              <li>Dimmable ambient lighting - warm spectrum</li>
              <li>Ergonomic seating, 20-minute optimised</li>
              <li>2-3 day deployment timeline</li>
              <li>20-25 sessions per day throughput</li>
            </ul>
          </div>

          <div className="feature-column reveal reveal-d3">
            <h3>Breathe Bar</h3>
            <span className="feature-subhead">200 SQFT • MULTI-POD</span>
            <ul>
              <li>200 sqft modular footprint</li>
              <li>2-3 individual private pods per unit</li>
              <li>Kids calm zone - sensory, not play</li>
              <li>Social space - passive companionship</li>
              <li>Self-serve bar - water + espresso</li>
              <li>Pure air throughout - filtered environment</li>
              <li>Mix-and-match experience types</li>
              <li>Council and landlord pitch ready</li>
              <li>Measurable social impact</li>
            </ul>
          </div>
        </div>
      </section>

      {/* 17. FINAL IMAGE — triggers chatbot */}
      <section
        className="cas1-image-break cas1-final-image"
        style={{ backgroundImage: "url(/images/IMG_5805.PNG)" }}
        ref={gatewayRef}
      >
        <div className="cas1-footer">© 2026 LOA</div>
      </section>
    </>
  );
}
